import { useCallback, useEffect, useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const url = () => `${API.replace(/^http/, "ws")}/api/ws/trading?token=${encodeURIComponent(localStorage.getItem("felacoo_token") || "")}`;

// Browser side of the streaming ticket (/api/ws/trading): ONE socket per ticket; send parameters, receive every Deriv
// repricing. Reconnects with backoff and re-sends the last ticket. Also measures browser ↔ Fela RTT with app-level pings.
export function useTradingSocket(active = true) {
  const ws = useRef(null);
  const lastTicket = useRef(null);
  const backoff = useRef(1000);
  const [connected, setConnected] = useState(false);
  const [quote, setQuote] = useState(null); // { proposal, params, atMs, rttMs, updates, receivedAt }
  const [checks, setChecks] = useState([]);
  const [error, setError] = useState(null); // { step, message }
  const [browserRtt, setBrowserRtt] = useState(null);
  const [derivOffset, setDerivOffset] = useState(null); // deriv_time_ms - Date.now()

  useEffect(() => {
    if (!active) return undefined;
    let closed = false, timer = null, pinger = null;
    const connect = () => {
      const sock = new WebSocket(url());
      ws.current = sock;
      sock.onopen = () => {
        setConnected(true); backoff.current = 1000;
        if (lastTicket.current) sock.send(JSON.stringify({ ticket: lastTicket.current }));
        pinger = setInterval(() => { if (sock.readyState === 1) sock.send(JSON.stringify({ ping: Date.now() })); }, 10000);
        sock.send(JSON.stringify({ ping: Date.now() }));
      };
      sock.onmessage = (ev) => {
        const m = JSON.parse(ev.data);
        if (m.type === "proposal") {
          const cur = lastTicket.current;
          if (cur && (Number(m.params.stake) !== Number(cur.stake) || m.params.contract_type !== cur.contract_type || m.params.duration !== Number(cur.duration) || (m.params.barrier || null) !== (cur.barrier || null) || m.params.symbol !== cur.symbol)) return; // stale ticket
          setQuote({ proposal: m.proposal, params: m.params, atMs: m.at_ms, rttMs: m.rtt_ms, updates: m.updates, receivedAt: Date.now() });
          setError(null);
          if (m.deriv_time_ms) setDerivOffset(m.deriv_time_ms - Date.now());
        } else if (m.type === "ticket_ok") { setChecks(m.checks || []); setError(null); }
        else if (m.type === "proposal_error") { setError({ step: m.step, message: m.message }); setQuote(null); }
        else if (m.type === "pong") { setBrowserRtt(Date.now() - m.t); if (m.deriv_time_ms) setDerivOffset(m.deriv_time_ms - Date.now()); }
        else if (m.type === "cleared") { setQuote(null); }
      };
      sock.onclose = () => {
        setConnected(false); clearInterval(pinger);
        if (closed) return;
        timer = setTimeout(connect, backoff.current);
        backoff.current = Math.min(backoff.current * 2, 15000);
      };
      sock.onerror = () => sock.close();
    };
    connect();
    return () => { closed = true; clearTimeout(timer); clearInterval(pinger); ws.current?.close(); ws.current = null; };
  }, [active]);

  const sendTicket = useCallback((ticket) => {
    lastTicket.current = ticket;
    setQuote(null);
    if (ws.current?.readyState === 1) ws.current.send(JSON.stringify({ ticket }));
  }, []);
  const clear = useCallback(() => {
    lastTicket.current = null; setQuote(null); setError(null);
    if (ws.current?.readyState === 1) ws.current.send(JSON.stringify({ clear: 1 }));
  }, []);

  return { connected, quote, checks, error, browserRtt, derivOffset, sendTicket, clear };
}
